import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { entities } from "byzantine";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import { UUID_PATTERN } from "../utils";

type UseDualApprovalDrawerNavigationOptions = {
  items: PaginatedDualApproval[];
  basePath: string;
  loading: boolean;
  onFetchError?: () => void;
};

const useDualApprovalDrawerNavigation = ({
  items,
  basePath,
  loading,
  onFetchError,
}: UseDualApprovalDrawerNavigationOptions) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dualApprovalId: selectedDualApprovalId } = useParams();
  const selectedDrawerId =
    searchParams.get("dualApprovalId") || selectedDualApprovalId;
  const selectedDrawerApprovalId = UUID_PATTERN.test(selectedDrawerId || "")
    ? (selectedDrawerId as API.DualApprovalId)
    : undefined;

  const { send: fetchDualApprovalById } =
    entities.dualApprovals.useFetchDualApprovalById();
  const fetchedDualApproval = entities.dualApprovals.useFormattedDualApproval(
    selectedDrawerApprovalId
  );

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [requestedDrawerId, setRequestedDrawerId] = useState<string | null>(
    null
  );

  const getPathWithDrawer = useCallback(
    (dualApprovalId?: string) => {
      if (!dualApprovalId) {
        return basePath;
      }
      return `${basePath}?dualApprovalId=${dualApprovalId}`;
    },
    [basePath]
  );

  useEffect(() => {
    if (!selectedDrawerId) {
      setSelectedIndex(-1);
      setRequestedDrawerId(null);
      return undefined;
    }

    if (!selectedDrawerApprovalId) {
      setSelectedIndex(-1);
      setRequestedDrawerId(null);
      navigate(basePath, { replace: true });
      return undefined;
    }

    const index = items.findIndex((item) => item.id === selectedDrawerId);

    if (index > -1) {
      setSelectedIndex(index);
      setRequestedDrawerId(null);
      return undefined;
    }

    setSelectedIndex(-1);

    if (loading || requestedDrawerId === selectedDrawerId) {
      return undefined;
    }

    let isActive = true;

    const fetchSelectedDualApproval = async () => {
      setRequestedDrawerId(selectedDrawerId);

      try {
        const dualApproval = await fetchDualApprovalById(
          selectedDrawerApprovalId
        );

        if (!dualApproval && isActive) {
          navigate(basePath, { replace: true });
        }
      } catch {
        if (isActive) {
          onFetchError?.();
          navigate(basePath, { replace: true });
        }
      }
    };

    fetchSelectedDualApproval();

    return () => {
      isActive = false;
    };
  }, [
    basePath,
    fetchDualApprovalById,
    items,
    loading,
    navigate,
    onFetchError,
    requestedDrawerId,
    selectedDrawerApprovalId,
    selectedDrawerId,
  ]);

  const activeDualApproval =
    selectedIndex > -1 ? items[selectedIndex] : fetchedDualApproval;
  const showControls = selectedIndex > -1 && items.length > 1;
  const isDrawerOpen = Boolean(selectedDrawerId && activeDualApproval);

  const handleOpenDrawer = useCallback(
    (dualApprovalId: string) => {
      navigate(getPathWithDrawer(dualApprovalId));
    },
    [getPathWithDrawer, navigate]
  );

  const handleCloseDrawer = useCallback(() => {
    navigate(basePath);
    setSelectedIndex(-1);
  }, [basePath, navigate]);

  const navigateToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= items.length) {
        handleCloseDrawer();
      } else {
        navigate(getPathWithDrawer(items[index].id));
      }
    },
    [getPathWithDrawer, handleCloseDrawer, items, navigate]
  );

  const handleOnNext = useCallback(() => {
    navigateToIndex(selectedIndex + 1);
  }, [navigateToIndex, selectedIndex]);

  const handleOnPrev = useCallback(() => {
    navigateToIndex(selectedIndex - 1);
  }, [navigateToIndex, selectedIndex]);

  return {
    selectedIndex,
    activeDualApproval,
    showControls,
    isDrawerOpen,
    handleOpenDrawer,
    handleCloseDrawer,
    handleOnNext,
    handleOnPrev,
    getPathWithDrawer,
  };
};

export default useDualApprovalDrawerNavigation;
